/** Context kind used for user identification and flag targeting. */
export const USER_CONTEXT_KIND = 'user';

/**
 * Payload for the user context (nested under multi-context).
 * Used for flag targeting by profile and org/hub/fleet.
 */
export type UserContextPayload = {
  kind: typeof USER_CONTEXT_KIND;
  key: string;
  role?: string;
  organizationId?: string;
  hubId?: string;
  fleetId?: string;
  language?: string;
  countryOfOperation?: string;
};

/** Input for building a user context; all values are plain strings for app-agnostic use. */
export type BuildUserContextInput = {
  id: string;
  role?: string;
  organizationId?: string;
  hubId?: string;
  fleetId?: string;
  language?: string;
  countryOfOperation?: string;
};

/**
 * Builds the user context payload for setContext so that flag targeting
 * (e.g. by org ID) works consistently across apps.
 *
 * The context key is always the user's id, so individual flag targeting by
 * user id resolves the same identity whether or not the user is impersonating.
 *
 * @param input - User identifiers (id required; role, org/hub/fleet optional)
 * @returns Payload for setContext(USER_CONTEXT_KIND, payload), or null if id missing
 */
export function buildUserContext(
  input: BuildUserContextInput | null | undefined
): UserContextPayload | null {
  if (!input?.id) return null;

  return {
    kind: USER_CONTEXT_KIND,
    key: input.id,
    role: input.role,
    organizationId: input.organizationId,
    hubId: input.hubId,
    fleetId: input.fleetId,
    language: input.language,
    countryOfOperation: input.countryOfOperation,
  };
}
